import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { billingService } from '../../../api';
import { TopProgress, Table, Typography, Button } from '../../../components';
import { timeConverterMMDDYYHHMM } from '../../../utils/dates';
import { formatCurrency } from '../../../utils/display-price';
import { useUi } from '../../../context/ui.context';
import { WarningBox, WarningIcon, WarningText, CyclesActions } from './styles';
import { icons } from '../../../icons';

type ServiceCyclesTabProps = {
  serviceId: string;
}

const DEFAULT_PAGE_SIZE = 10;

const ServiceCyclesTab: FC<ServiceCyclesTabProps> = ({ serviceId }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [ordering] = useQueryParam('ordering', StringParam);
  const [loading, setLoading] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [paginationData, setPaginationData] = useState<any>();
  const [cycles, setCycles] = useState<any[]>([]);

  useEffect(() => {
    if (!page) {
      setPage(1);
    }
    if (!pageSize) {
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  }, [page, pageSize, setPage, setPageSize]);

  const fetchCycles = () => {
    setLoading(true);
    billingService.getServiceCycles(serviceId, {
      page: page || 1,
      page_size: pageSize || DEFAULT_PAGE_SIZE,
      ordering: ordering || '-id',
    })
      .then((res: any) => {
        setPaginationData(res.data);
        setCycles(res.data.objects.map((cycle: any) => ({
          ...cycle,
          id: cycle.id,
          state: (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant='body'>{cycle.state}</Typography>
              {cycle.invoice_id ? (
                <a href={`/billing/invoices/${cycle.invoice_id}`}>#{cycle.invoice_id}</a>
              ) : (
                <Typography variant='small'>{t('N/A', { defaultValue: 'n/a' })}</Typography>
              )}
            </div>
          ),
          start_dt: timeConverterMMDDYYHHMM(cycle.start_dt),
          end_dt: cycle.end_dt ? timeConverterMMDDYYHHMM(cycle.end_dt) : t('N/A', { defaultValue: 'n/a' }),
          price: formatCurrency(cycle.price, cycle.currency),
          fixed_price: formatCurrency(cycle.fixed_price, cycle.currency),
          dynamic_price: formatCurrency(cycle.dynamic_price, cycle.currency),
          type: cycle.type,
          billing_type: cycle.billing_type,
        })));
      })
      .catch((err: any) => {
        if (err?.response?.data?.detail) {
          viewAlert({
            severity: 'error',
            message: err.response.data.detail,
          });
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCycles();
  }, [serviceId, page, pageSize, ordering]);

  const handleAddNewCycle = async () => {
    const confirmed = window.confirm(
      t('CREATE_NEW_SERVICE_CYCLE_CONFIRM', {
        defaultValue: 'Create a new service cycle for this service?',
      })
    );
    if (!confirmed) return;
    setCreating(true);
    try {
      const res = await billingService.createNewServiceCycle(serviceId);
      if (res?.data?.detail) {
        viewAlert({
          severity: 'info',
          message: res.data.detail,
        });
      }
      fetchCycles();
    } catch (err: any) {
      if (err?.response?.data?.detail) {
        viewAlert({
          severity: 'error',
          message: err.response.data.detail,
        });
      }
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    { title: 'ID', key: 'id', sorted: true },
    { title: t('STATE'), key: 'state', sorted: true },
    { title: t('START_DATE'), key: 'start_dt', sorted: true },
    { title: t('END_DATE'), key: 'end_dt', sorted: true },
    { title: t('PRICE'), key: 'price', sorted: true },
    { title: t('FIXED_PRICE'), key: 'fixed_price', sorted: true },
    { title: t('DYNAMIC_PRICE'), key: 'dynamic_price', sorted: true },
    { title: t('TYPE'), key: 'type', sorted: true },
    { title: t('BILLING_TYPE'), key: 'billing_type', sorted: true },
  ];

  return (
    <>
      <TopProgress loading={loading || creating} />
      <WarningBox>
        <WarningIcon src={icons.Warning} />
        <WarningText>
          {t('SERVICE_CYCLE_EDIT_WARNING', {
            defaultValue: 'Editing cycles may interfere with automatic billing of this service. ' +
              'Process clients periodic task may also update cycles. ' +
              'To prevent possible issues it is recommended you edit cycles when process clients cron periodic task is not running.',
          })}
        </WarningText>
      </WarningBox>

      <CyclesActions>
        <Typography variant='h2'>{t('SERVICE_CYCLES', { defaultValue: 'Service cycles' })}</Typography>
        <Button
          variant='primary'
          size='small'
          title={t('ADD_NEW_CYCLE', { defaultValue: 'Add new cycle' })}
          onClick={handleAddNewCycle}
          disabled={creating}
        >
          {t('ADD_NEW_CYCLE', { defaultValue: 'Add new cycle' })}
        </Button>
      </CyclesActions>

      <Table
        columns={columns}
        rows={cycles}
        hideCheckbox
        paginationData={paginationData}
        loading={loading}
      />
    </>
  );
};

export default ServiceCyclesTab;
